import { useCallback, useEffect, useRef, useState } from 'react';
import { useFileUpload } from '@hooks/useFileUpload';
import { useScadaUploadUpdate } from '@hooks/graphql/useScadaDataUpdate';
import { useScadaScanStatusPoll } from '@hooks/useScadaScanStatusPoll';
import useSession from '@hooks/useSession';
import { useApi } from '@hooks/useApi';

export const MeterReadDetailsFileCellStatus = {
  NO_DATA: 'NO_DATA',
  UPLOAD_ONLY: 'UPLOAD_ONLY',
  DOWNLOAD_ONLY: 'DOWNLOAD_ONLY',
  BOTH: 'BOTH',
  NOTHING: 'NOTHING',
} as const;

const useUpdateMeterDetailsBlade = ({ id, data, isEffectiveMonth, isApproved, onUploadComplete }: { id: any; data: any; isEffectiveMonth: any; isApproved: any; onUploadComplete?: () => void }) => {

  const { uploadFile, uploading, success, error, batchId } = useFileUpload(false, {
    isApproved,
    isEffectiveMonth,
  });

  const { updateScadaUpload } = useScadaUploadUpdate();
  const { account } = useSession();
  const { fetchScreenshotDownloadUrl } = useApi();
  const { scanProgress, resetScan } = useScadaScanStatusPoll(batchId);

  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [scadaScanError, setScadaScanError] = useState<string | null>(null);
  const uploadProcessedRef = useRef(false);

  const month = data?.month_year?.split('-')[1];
  const year = data?.month_year?.split('-')[0];

  let status;
  if (!data) {
    status = MeterReadDetailsFileCellStatus.NO_DATA;
  } else if (isEffectiveMonth && !data.value) {
    status = MeterReadDetailsFileCellStatus.UPLOAD_ONLY;
  } else if (!isEffectiveMonth && data.value) {
    status = MeterReadDetailsFileCellStatus.DOWNLOAD_ONLY;
  } else if (isEffectiveMonth && data.value) {
    status = MeterReadDetailsFileCellStatus.BOTH;
  } else {
    status = MeterReadDetailsFileCellStatus.NOTHING;
  }

  // The uploaded file is held for malware scanning before the SCADA_upload
  // record is saved — saving immediately would risk persisting a reference to
  // a file that Defender for Storage later flags and deletes. Once the scan
  // resolves Clean, fire the exact same updateScadaUpload call this used to
  // fire right after upload; on Malicious, surface an error instead of saving.
  useEffect(() => {
    if (!success) {
      uploadProcessedRef.current = false;
      setScadaScanError(null);
      return;
    }
    if (isApproved && isEffectiveMonth) return;
    if (!(success === true && uploadedFileName && !uploadProcessedRef.current)) return;

    // No batchId means the backend didn't hand back a scan batch (shouldn't
    // normally happen) — fall back to saving immediately rather than hanging.
    const stillScanning = !!batchId && scanProgress.status !== 'clean' && scanProgress.status !== 'malicious';
    if (stillScanning) return;

    if (scanProgress.status === 'malicious') {
      uploadProcessedRef.current = true;
      setScadaScanError('Malicious file detected — upload blocked and file removed.');
      resetScan();
      return;
    }

    uploadProcessedRef.current = true;
    setScadaScanError(null);
    const currentUser = account?.username || account?.name || 'unknown';
    const uploadedAt = new Date().toISOString();

    // Update or create SCADA upload record
    updateScadaUpload(
      [
        {
          SCADA_upload_id: data?.id || null,
          meter_id: typeof id === 'string' ? Number.parseInt(id, 10) : id,
          month_year: data?.month_year,
          file_name: uploadedFileName || undefined,
          uploaded_by: currentUser,
          uploaded_at: uploadedAt,
        },
      ],
      data?.id
        ? 'File uploaded via meter read details table'
        : 'File uploaded and SCADA upload record created via meter read details table'
    ).then(() => {
      onUploadComplete?.();
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[useUpdateMeterDetailsBlade] updateScadaUpload failed:', err);
    });
  }, [
    success,
    error,
    batchId,
    scanProgress.status,
    data?.id,
    data?.month_year,
    id,
    uploadedFileName,
    updateScadaUpload,
    account,
    onUploadComplete,
    resetScan,
  ]);

  const handleUpload = useCallback(
    async (info) => {

      if (isApproved && isEffectiveMonth) {
        return;
      }
      if (uploading) {
        return;
      }

      const file = info.file.originFileObj || info.file;

      setUploadedFileName(file.name);
      uploadFile(file, { meterId: id, month, year });
    },
    [uploadFile, id, month, year, uploading]
  );

  const triggerUpload = useCallback(
    (file: File): false => {
      if (!isApproved || !isEffectiveMonth) {
        if (!uploading) {
          setUploadedFileName(file.name);
          void uploadFile(file, { meterId: id, month, year });
        }
      }
      return false;
    },
    [uploadFile, id, month, year, uploading, isApproved, isEffectiveMonth]
  );

  const handleDownload = useCallback(async () => {
    if (downloading) {
      return;
    }

    if (!year || !month) {
      return null;
    }

    setDownloading(true);
    try {
      const yearNum = Number.parseInt(year, 10);
      const monthNum = Number.parseInt(month, 10);

      if (Number.isNaN(yearNum) || Number.isNaN(monthNum)) {
        throw new TypeError(`Invalid year or month: ${year}, ${month}`);
      }

      const url = await fetchScreenshotDownloadUrl(id, yearNum, monthNum);

      if (url) {
        return url;
      } else {
        return null;
      }
    } catch {
      return null;
    } finally {
      setDownloading(false);
    }
  }, [downloading, data?.value, fetchScreenshotDownloadUrl, id, year, month]);
  if (isApproved && isEffectiveMonth) {
    return {
      status: data.value
        ? MeterReadDetailsFileCellStatus.DOWNLOAD_ONLY
        : MeterReadDetailsFileCellStatus.NOTHING,
      handleUpload: () => { },
      triggerUpload: () => false as const,
      uploading: false,
      handleDownload,
      uploadSuccess: false,
      scanning: false,
      scadaScanError: null,
    };
  }

  // uploadSuccess now means "safe to view/reference" — i.e. the scan resolved
  // Clean — not just "the blob PUT succeeded", since a Malicious file is deleted.
  const uploadSuccess = scanProgress.status === 'clean' || (success === true && !batchId);

  return {
    status,
    handleUpload,
    triggerUpload,
    uploading,
    handleDownload,
    uploadSuccess,
    scanning: scanProgress.status === 'scanning',
    scadaScanError,
  };
};

export default useUpdateMeterDetailsBlade;
